/**
 * @fileoverview Root application component with routing and core providers
 * Manages application initialization, loading states, and route transitions
 */

import * as React from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { ThemeProvider } from "@/hooks/use-theme";
import { FontSizeProvider } from "@/hooks/use-font-size";
import { AnimatePresence, motion } from "framer-motion";
import { useThemeMeta } from "@/hooks/use-theme-meta";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { I18nextProvider } from "react-i18next";
import i18n, { dynamicActivate } from "./lib/i18n";
import { LanguageProvider } from "./lib/language-context";
import { SEOMetadata } from "@/components/ui/seo-metadata";
import { FeedbackTrackerProvider } from "@/hooks/use-feedback-tracker";
// Main components and providers

// Direct imports for critical components
import Home from "@/pages/home";
import ServerError from "./pages/ServerError";
import Forbidden from "./pages/Forbidden";
import NotFound from "./pages/NotFound";
import AuthPage from "@/pages/auth-page";
import FeedbackDashboard from "@/pages/admin/feedback-dashboard";
import { AuthProvider } from "@/hooks/use-auth";

// Cache control wrapper component
const NoCacheRoute: React.FC<{ component: React.ComponentType }> = ({ component: Component }) => {
  React.useEffect(() => {
    // Add cache control meta tags
    const meta = document.createElement('meta');
    meta.setAttribute('http-equiv', 'Cache-Control');
    meta.setAttribute('content', 'no-cache, no-store, must-revalidate');
    document.head.appendChild(meta);

    return () => {
      meta.remove();
    };
  }, []);

  return <Component />;
};

function LoadingScreen() {
  // Use ref for image preloading to avoid re-renders
  const preloadedRef = React.useRef(false);
  // Track animation readiness separately from component mounting
  const [animationReady, setAnimationReady] = React.useState(false);
  // Track if image is actually loaded
  const [imageLoaded, setImageLoaded] = React.useState(false);
  
  React.useEffect(() => {
    // Preload the image before showing animation to ensure consistent experience
    if (!preloadedRef.current) {
      preloadedRef.current = true;
      
      // Create an image element to preload
      const img = new Image();
      img.onload = () => {
        // Only set loaded state once image is fully loaded
        setImageLoaded(true);
      };
      img.onerror = () => {
        // Fallback in case of error - still show animation without logging
        setImageLoaded(true);
      };
      
      // Actually load the image
      img.src = '/assets/ui/LOADER-MJALLAL.webp';
      
      // Use triple-nested rAF to ensure browser has painted frames
      // This helps avoid animation jank by ensuring we're not animating during layout
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setAnimationReady(true);
          });
        });
      });
    }
    
    return () => {
      // Cleanup - no need to do anything specific here
    };
  }, []);

  // Only render once everything is ready
  if (!animationReady || !imageLoaded) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ 
        opacity: 1,
        transition: { 
          duration: 0.35, 
          ease: [0.22, 1, 0.36, 1] // Custom ease inspired by CSS cubic-bezier
        }
      }}
      exit={{ 
        opacity: 0,
        transition: { 
          duration: 0.4, 
          ease: [0.65, 0, 0.35, 1] // Smoother exit animation
        }
      }}
      className="preloader-container"
    >
      <motion.div 
        className="preloader-wrapper"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ 
          scale: 1,
          opacity: 1,
          transition: {
            duration: 0.6,
            ease: "easeOut",
            opacity: { duration: 0.3 }
          }
        }}
        exit={{ 
          scale: 1.1,
          opacity: 0,
          transition: {
            duration: 0.4,
            ease: "easeIn"
          }
        }}
      >
        <img
          src="/assets/ui/LOADER-MJALLAL.webp"
          alt="MJ Logo"
          className="preloader-logo preloader-pulse"
          loading="eager"
          decoding="async"
          // Priority already handled via loading="eager"
        />
      </motion.div>
    </motion.div>
  );
}

function AppContent() {
  // Always call hooks in the same order - fixes "Rendered more hooks than during previous render" error
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [allAssetsLoaded, setAllAssetsLoaded] = React.useState(false);
  const [i18nLoaded, setI18nLoaded] = React.useState(false);
  // Application state management
  const unmountingRef = React.useRef(false);
  // Track performance metrics
  const loadStartTime = React.useRef(performance.now());
  // Set theme color to match loader background - called unconditionally to maintain hook order
  useThemeMeta();
  // Performance optimization refs
  const imageObserverRef = React.useRef<IntersectionObserver | null>(null);
  const pendingTimersRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);
  // Memory cleanup tracker
  const cleanupTasksRef = React.useRef<Array<() => void>>([]);
  // Minimum loading time to avoid too-quick flashes (ms)
  const MIN_LOADING_TIME = 800;

  // Application initialization

  React.useEffect(() => {
    const initI18n = async () => {
      try {
        const supportedLocales = ['en', 'ar', 'zh', 'ja', 'ru', 'de', 'fr', 'es', 'hi', 'tr', 'sv'];
        let locale = 'en'; // Default fallback
        
        // First: Check for language in URL path (preferred new method)
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const pathLang = pathParts[0];
        
        if (pathLang && supportedLocales.includes(pathLang)) {
          // Priority 1: Use language from URL path
          locale = pathLang;
        } else {
          // Second: Check for language in URL query parameter (legacy method)
          const urlParams = new URLSearchParams(window.location.search);
          const queryLang = urlParams.get('lang');
          
          if (queryLang && supportedLocales.includes(queryLang)) {
            // Priority 2: Use language from URL query parameter
            locale = queryLang;
          } else {
            // Third: Check for language in localStorage (fallback)
            const storedLocale = localStorage.getItem('locale');
            if (storedLocale && supportedLocales.includes(storedLocale)) {
              // Priority 3: Use language from localStorage
              locale = storedLocale;
            }
            // Otherwise use default language (already set)
          }
        }
        
        await dynamicActivate(locale as any);
        setI18nLoaded(true);
      } catch (error) {
        console.error('[i18n] Error initializing:', error);
        // Still mark as loaded to allow app to progress
        setI18nLoaded(true);
      }
    };

    // Initialize i18n immediately
    initI18n();
    
    // Prepare for asset loading - optimized with memory management
    const prepareCriticalAssets = () => {
      // First, register intersection observer to preload images as they come into view
      if ('IntersectionObserver' in window) {
        // Create and store observer in the ref to ensure proper cleanup
        imageObserverRef.current = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target as HTMLImageElement;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                imageObserverRef.current?.unobserve(img);
              }
            }
          });
        }, { rootMargin: '100px' });
        
        // Track images to ensure proper cleanup
        const lazyImages = document.querySelectorAll('img[data-src]');
        
        // Observe all lazy-loaded images
        lazyImages.forEach(img => {
          imageObserverRef.current?.observe(img);
        });
        
        // Add cleanup task for observer and listeners
        cleanupTasksRef.current.push(() => {
          if (imageObserverRef.current) {
            lazyImages.forEach(img => {
              imageObserverRef.current?.unobserve(img);
            });
            imageObserverRef.current.disconnect();
            imageObserverRef.current = null;
          }
        });
      }
      
      // Mark assets as initially loaded
      setAllAssetsLoaded(true);
      
      // Continue with comprehensive asset verification - memory optimized
      const trackRemainingAssets = () => {
        const images = document.querySelectorAll('img:not([data-src])');
        
        // Only track non-completed images to reduce memory usage
        const imagePromises: Promise<void>[] = [];
        const imageListeners: { img: HTMLImageElement, listeners: Array<{ event: string, handler: () => void }> } = 
          { img: document.createElement('img'), listeners: [] };
        
        Array.from(images).forEach(imgEl => {
          const img = imgEl as HTMLImageElement;
          if (!img.complete) {
            const loadPromise = new Promise<void>(resolve => {
              const loadHandler = () => resolve();
              const errorHandler = () => resolve();
              
              img.addEventListener('load', loadHandler, { once: true });
              img.addEventListener('error', errorHandler, { once: true });
              
              // Track listeners for cleanup
              imageListeners.listeners.push(
                { event: 'load', handler: loadHandler },
                { event: 'error', handler: errorHandler }
              );
            });
            
            imagePromises.push(loadPromise);
          }
        });
        
        // Add cleanup function for image event listeners
        cleanupTasksRef.current.push(() => {
          imageListeners.listeners.forEach(({ event, handler }) => {
            imageListeners.img.removeEventListener(event, handler);
          });
        });
        
        // Use Promise.allSettled for better error handling
        Promise.allSettled(imagePromises).then(() => {
          if (unmountingRef.current) return;
          
          // Calculate elapsed time to avoid too-quick transitions
          const elapsedTime = performance.now() - loadStartTime.current;
          const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
          
          // Use a smooth transition with minimum duration
          const timerId = setTimeout(() => {
            if (!unmountingRef.current) {
              // Fade out the preloader
              setInitialLoad(false);
            }
          }, remainingTime);
          
          // Track timer for cleanup
          pendingTimersRef.current.push(timerId);
        });
      };
      
      // Use requestIdleCallback if available for non-critical work
      if (window.requestIdleCallback) {
        const callbackId = window.requestIdleCallback(trackRemainingAssets, { timeout: 1000 });
        // Add cleanup task for idle callback
        cleanupTasksRef.current.push(() => {
          window.cancelIdleCallback?.(callbackId);
        });
      } else {
        // Fallback to setTimeout for older browsers
        const timerId = setTimeout(trackRemainingAssets, 100);
        pendingTimersRef.current.push(timerId);
      }
    };
    
    // Nest in triple requestAnimationFrame for smoother transitions
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(prepareCriticalAssets);
      });
    });
    
    // Comprehensive cleanup function to prevent memory leaks
    return () => {
      // Mark as unmounting to prevent further state updates
      unmountingRef.current = true;
      
      // Clear all pending timers
      pendingTimersRef.current.forEach(timerId => {
        clearTimeout(timerId);
      });
      pendingTimersRef.current = [];
      
      // Execute all registered cleanup tasks
      cleanupTasksRef.current.forEach(cleanupTask => {
        try {
          cleanupTask();
        } catch (error) {
          console.error('[Cleanup] Error during cleanup:', error);
        }
      });
      cleanupTasksRef.current = [];
      
      // Cleanup is already handled via the registered tasks
      // No additional idle callback cancellation needed here
    };
  }, []);

  // Render loading screen if i18n is still initializing
  // This is now a separate component without conditional hook usage
  const renderContent = () => {
    if (!i18nLoaded) {
      return <LoadingScreen />;
    }

    return (
      <main className="flex flex-col min-h-screen app-content">
        {/* Dynamic SEO Metadata Component */}
        <SEOMetadata />
        
        
        <ErrorBoundary>
          <AnimatePresence mode="sync">
            {initialLoad && <LoadingScreen key="initial-loading" />}
            <motion.article
              key="content"
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: allAssetsLoaded ? 1 : 0,
                transition: {
                  duration: 0.5,
                  ease: "easeOut",
                  delay: 0.1
                }
              }}
              className="flex-1 w-full"
            >
              <Switch>
                {/* Language-specific routes with path-based routing structure */}
                <Route path="/ar" component={Home} />
                <Route path="/ar/:rest*" component={Home} />
                
                <Route path="/en" component={Home} />
                <Route path="/en/:rest*" component={Home} />
                
                <Route path="/zh" component={Home} />
                <Route path="/zh/:rest*" component={Home} />
                
                <Route path="/ja" component={Home} />
                <Route path="/ja/:rest*" component={Home} />
                
                <Route path="/ru" component={Home} />
                <Route path="/ru/:rest*" component={Home} />
                
                <Route path="/de" component={Home} />
                <Route path="/de/:rest*" component={Home} />
                
                <Route path="/fr" component={Home} />
                <Route path="/fr/:rest*" component={Home} />
                
                <Route path="/es" component={Home} />
                <Route path="/es/:rest*" component={Home} />
                
                <Route path="/hi" component={Home} />
                <Route path="/hi/:rest*" component={Home} />
                
                <Route path="/tr" component={Home} />
                <Route path="/tr/:rest*" component={Home} />
                
                <Route path="/sv" component={Home} />
                <Route path="/sv/:rest*" component={Home} />
                
                {/* Default (root) route */}
                <Route path="/" component={Home} />
                
                {/* Authentication and Admin routes */}
                <Route path="/auth" component={AuthPage} />
                <Route path="/admin/feedback" component={FeedbackDashboard} />
                
                {/* Error routes */}
                <Route path="/500" component={(props) => <NoCacheRoute component={ServerError} {...props} />} />
                <Route path="/403" component={(props) => <NoCacheRoute component={Forbidden} {...props} />} />
                <Route component={(props) => <NoCacheRoute component={NotFound} {...props} />} />
              </Switch>
            </motion.article>
          </AnimatePresence>
          <Toaster />
        </ErrorBoundary>
      </main>
    );
  };

  // Always return the same function for all code paths
  return renderContent();
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <FontSizeProvider>
          <QueryClientProvider client={queryClient}>
            <I18nextProvider i18n={i18n}>
              <LanguageProvider>
                <AuthProvider>
                  <FeedbackTrackerProvider>
                    <AppContent />
                  </FeedbackTrackerProvider>
                </AuthProvider>
              </LanguageProvider>
            </I18nextProvider>
          </QueryClientProvider>
        </FontSizeProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}